<template>
  <div id="app">
    <router-view />
    <portal-target name="app-root"></portal-target>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import * as Sentry from '@sentry/vue'
import Drawers from '@/components/Elements/Drawer/Drawers.vue'
import {
  clearEdits,
  guardWhileMounted,
  isUnsafeToReload,
  trackEdits
} from '@/utils/reloadSafety'
import {
  BUILD_SHA,
  fetchDeployedSha,
  startVersionCheck,
  VersionCheck
} from '@/utils/versionCheck'

@Component({
  components: {
    Drawers
  }
})
export default class App extends Vue {
  // Declared without initialisers so Vue leaves them unobserved.
  versionCheck!: VersionCheck
  noticeIds!: string[]
  saveFirstId!: string
  stopTrackingEdits!: () => void
  stopClearingEdits!: () => void

  mounted() {
    this.startVersionCheck()
    if (window) {
      // Detect if on IOS then apply a special class to
      if (
        [
          'iPad Simulator',
          'iPhone Simulator',
          'iPod Simulator',
          'iPad',
          'iPhone',
          'iPod'
        ].includes(navigator.platform)
      ) {
        document.querySelector('body').classList.add('ios-device')
      }

      // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
      let vh = window.innerHeight * 0.01
      // Then we set the value in the --vh custom property to the root of the document
      document.documentElement.style.setProperty('--vh', `${vh}px`)

      // We listen to the resize event
      window.addEventListener('resize', () => {
        // We execute the same script as before
        vh = window.innerHeight * 0.01
        document.documentElement.style.setProperty('--vh', `${vh}px`)
      })
    }
  }

  beforeDestroy() {
    this.versionCheck.stop()
    this.stopTrackingEdits()
    this.stopClearingEdits()
  }

  startVersionCheck() {
    this.noticeIds = []
    this.stopTrackingEdits = trackEdits()
    // A query or hash change can keep the same view and its inputs, so only a new path clears.
    this.stopClearingEdits = this.$router.afterEach((to, from) => {
      if (to.path !== from.path) clearEdits()
    })
    guardWhileMounted(
      this,
      'drawer-open',
      () => this.$store.getters.drawers().length > 0
    )
    this.versionCheck = startVersionCheck({
      currentSha: BUILD_SHA,
      fetchLatestSha: () =>
        fetchDeployedSha(`${process.env.BASE_URL}version.json`),
      isUnsafe: () => isUnsafeToReload(),
      reload: () => window.location.reload(),
      onUpdate: () =>
        this.showNotice('versionCheck.updateAvailable', 'versionCheck.reload'),
      onCurrent: () => this.hideNotices(),
      onStuck: deployedSha =>
        Sentry.captureMessage(
          'Version check: build still differs after reload',
          {
            level: Sentry.Severity.Warning,
            extra: { runningSha: BUILD_SHA, deployedSha }
          }
        )
    })
  }

  /** @returns the toast id. */
  showNotice(messageKey: string, actionKey: string, confirmed = false) {
    const id = this.$toast.info(this.$t(messageKey) as string, {
      persist: true,
      action: {
        label: this.$t(actionKey) as string,
        onClick: () => this.onReloadClick(confirmed)
      }
    })
    this.noticeIds.push(id)

    return id
  }

  onReloadClick(confirmed: boolean) {
    if (this.versionCheck.reloadNow(confirmed)) return
    // One warning at a time: a second click replaces it instead of stacking another.
    this.noticeIds = this.noticeIds.filter(id => {
      if (id !== this.saveFirstId) return true
      this.$toast.hide(id)

      return false
    })
    this.saveFirstId = this.showNotice(
      'versionCheck.saveFirst',
      'versionCheck.reloadAnyway',
      true
    )
  }

  hideNotices() {
    this.noticeIds.forEach(id => this.$toast.hide(id))
    this.noticeIds = []
  }
}
</script>
